import { ColumnDef, Table } from "@tanstack/react-table"
import { useRef } from "react"
import DataTable from "Components/molecule/data-table"
import ColumnVisibilityDropdown from "Components/molecule/column-visibility-dropdown"
import { ExportButton, PrintButton } from "Components/atoms/buttons/report-buttons"
import Breadcrumb from "Components/organisms/breadcrumb"
import LoadingState from "Components/organisms/loading-state"
import { Container } from "reactstrap"
import { openReportView } from "utils/report-snapshot"

interface DataTableTemplateProps<T> {
    title: string
    breadcrumbItem: string

    actions?: React.ReactNode
    tableActions?: React.ReactNode
    filters?: React.ReactNode
    beforeTable?: React.ReactNode
    afterTable?: React.ReactNode
    modal?: React.ReactNode

    table: Table<T>
    columns: ColumnDef<T>[]

    isLoading: boolean
    loadingTitle?: string
    loadingSubtitle?: string

    // Shown as "N rows" above the table; defaults to the rows on screen
    rowCount?: number
    columnResizeMode?: 'onChange' | 'onEnd'

    stickyFoot?: boolean
    stickyHead?: boolean

    // Hide the show/hide columns picker (shown by default)
    hideColumnPicker?: boolean
    // Hide the Export / Print buttons (shown by default; they open <page>/export and <page>/print)
    hideReportButtons?: boolean
    // The page's own full CSV export, offered on the export page next to the table CSV
    fullExport?: () => void | Promise<void>
}

const DataTableTemplate = <T,>({
    title,
    breadcrumbItem,
    actions,
    tableActions,
    filters,
    beforeTable,
    afterTable,
    modal,

    table,
    columns,
    isLoading,
    loadingTitle,
    loadingSubtitle,

    rowCount,
    columnResizeMode = 'onEnd',

    stickyFoot = true,
    stickyHead = true,
    hideColumnPicker = false,
    hideReportButtons = false,
    fullExport,
}: DataTableTemplateProps<T>) => {
    const rows = rowCount ?? table.getRowModel().rows.length
    const filtersRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const openView = (kind: 'print' | 'export') => openReportView(kind, {
        title: breadcrumbItem,
        module: title,
        content: contentRef.current,
        filters: filtersRef.current,
        fullExport: kind === 'export' ? fullExport : undefined,
    })

    return (
        <div className="page-content">
            <Container fluid style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                {/* Breadcrumb + Actions */}
                <Breadcrumb
                    title={title}
                    breadcrumbItem={breadcrumbItem}
                    actions={actions}
                />

                {/* Filters */}
                {filters && (
                    <div className="section-container" ref={filtersRef}>
                        {filters}
                    </div>
                )}

                <div ref={contentRef} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                    {/* Before Table */}
                    {beforeTable && (
                        <div className="section-container">
                            {beforeTable}
                        </div>
                    )}

                    {/* Table */}
                    {/* The toolbar sits outside the horizontal scroll so it stays on the right of wide tables */}
                    <div className="section-container">
                        {isLoading ? (
                            <LoadingState
                                title={loadingTitle ?? "Loading Data"}
                                subtitle={loadingSubtitle ?? "Fetching information…"}
                            />
                        ) : (
                            <>
                                <div className="d-print-none" style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: '10px',
                                    justifyContent: 'space-between',
                                    marginBottom: '10px'
                                }}>
                                    <span className="text-muted">{rows.toLocaleString('en-CA')} row{rows === 1 ? '' : 's'}</span>
                                    <div className="d-flex flex-row flex-wrap align-items-center gap-2">
                                        {tableActions}
                                        {!hideColumnPicker && <ColumnVisibilityDropdown table={table} />}
                                        {!hideReportButtons && <>
                                            <ExportButton onClick={() => openView('export')} />
                                            <PrintButton onClick={() => openView('print')} />
                                        </>}
                                    </div>
                                </div>
                                <div style={{ overflowX: "auto" }}>
                                    <DataTable
                                        table={table}
                                        columns={columns}
                                        columnResizeMode={columnResizeMode}
                                        stickyFoot={stickyFoot}
                                        stickyHead={stickyHead}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {/* After Table */}
                    {afterTable && (
                        <div className="section-container">
                            {afterTable}
                        </div>
                    )}
                </div>

                {/* Modal slot */}
                {modal}
            </Container>
        </div>
    )
}

export default DataTableTemplate
